import type { Price } from '@montugroup/circuit-shared/types/index';
import { GoogleAnalyticsEventName } from '@montugroup/circuit-shared/types/index';
import { useCallback, useMemo } from 'react';

import { sendGoogleAnalyticsGTagEvent } from '@/hooks/analytics/useGoogleAnalytics';

/**
 * Product display info for the GA add-to-cart / remove-from-cart ecommerce
 * payload. The quantity store holds no product data, so the component setting
 * a quantity supplies it.
 */
export interface ProductCartEventInfo {
  name: string;
  price: Price;
  /**
   * Quantity to report in the GA payload. Omit to report the quantity being
   * set (the escript flow); umeds reports the prescribed per-order quantity.
   */
  quantity?: number;
  recommendation?: { impressionId: string; position: number };
}

// The event-name choice is inherited unchanged from the order medication
// controllers, quirks included: a decrease maps to ADD_TO_CART, everything
// else (including a first add, which has no previous entry) to REMOVE_FROM_CART.
export const sendCartEvent = (
  oldQuantity: number | undefined,
  newQuantity: number,
  cartEventInfo: ProductCartEventInfo,
) => {
  const isDecreasing = typeof oldQuantity === 'number' && oldQuantity > newQuantity;
  const quantity = cartEventInfo.quantity ?? newQuantity;

  sendGoogleAnalyticsGTagEvent(
    isDecreasing ? GoogleAnalyticsEventName.ADD_TO_CART : GoogleAnalyticsEventName.REMOVE_FROM_CART,
    {
      currency: 'AUD',
      value: Number(cartEventInfo.price) * quantity,
      items: [{ name: cartEventInfo.name, quantity, price: cartEventInfo.price }],
    },
  );
};

/**
 * GA cart events only fire for products present in the refill response,
 * matching the events the controllers used to send. Returns a lookup from
 * productId to the cart event info a card passes to `useProductQuantity`.
 *
 * `reportPrescribedQuantity` makes the payload report the product's prescribed
 * per-order quantity (the umeds flow) instead of the quantity being set.
 * @param products
 * @param root0
 * @param root0.reportPrescribedQuantity
 */
export const useGetCartEventInfo = <T extends { id: number; name: string; price: Price; quantity?: number }>(
  products: T[] | undefined,
  { reportPrescribedQuantity = false }: { reportPrescribedQuantity?: boolean } = {},
) => {
  const productsById = useMemo(() => new Map((products ?? []).map((product) => [product.id, product])), [products]);

  return useCallback(
    (productId: number, recommendation?: ProductCartEventInfo['recommendation']): ProductCartEventInfo | undefined => {
      const product = productsById.get(productId);

      if (!product) {
        return undefined;
      }

      return reportPrescribedQuantity
        ? { name: product.name, price: product.price, quantity: product.quantity, recommendation }
        : { name: product.name, price: product.price, recommendation };
    },
    [productsById, reportPrescribedQuantity],
  );
};
